import { CalculationMethod, Coordinates, PrayerTimes, type CalculationParameters } from "adhan";

export type PrayerKey = "fajr" | "dhuhr" | "asr" | "maghrib" | "isha";

export interface PrayerSlot {
  key: PrayerKey;
  time: Date;
}

export interface PrayerCity {
  id: string;
  lat: number;
  lng: number;
}

/** Short fallback city list (used when geolocation is denied/unavailable). */
export const PRAYER_CITIES: PrayerCity[] = [
  { id: "riyadh", lat: 24.7136, lng: 46.6753 },
  { id: "jeddah", lat: 21.4858, lng: 39.1925 },
  { id: "makkah", lat: 21.3891, lng: 39.8579 },
  { id: "dammam", lat: 26.4207, lng: 50.0888 },
  { id: "cairo", lat: 30.0444, lng: 31.2357 },
  { id: "dubai", lat: 25.2048, lng: 55.2708 },
  { id: "istanbul", lat: 41.0082, lng: 28.9784 },
  { id: "london", lat: 51.5072, lng: -0.1276 },
];

/** Pick a sane calculation method from the browser timezone; defaults to Umm al-Qura. */
function pickParams(): CalculationParameters {
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    tz = "";
  }
  if (tz.startsWith("Africa/Cairo")) return CalculationMethod.Egyptian();
  if (tz.startsWith("Europe/Istanbul")) return CalculationMethod.Turkey();
  if (tz.startsWith("Asia/Karachi") || tz.startsWith("Asia/Kolkata")) return CalculationMethod.Karachi();
  if (tz.startsWith("Asia/Dubai")) return CalculationMethod.Dubai();
  if (tz.startsWith("Europe/") || tz.startsWith("America/")) return CalculationMethod.MuslimWorldLeague();
  return CalculationMethod.UmmAlQura();
}

/** All five prayer times for the given day at the given coordinates. */
export function getDayPrayers(lat: number, lng: number, day: Date = new Date()): PrayerSlot[] {
  const times = new PrayerTimes(new Coordinates(lat, lng), day, pickParams());
  return [
    { key: "fajr", time: times.fajr },
    { key: "dhuhr", time: times.dhuhr },
    { key: "asr", time: times.asr },
    { key: "maghrib", time: times.maghrib },
    { key: "isha", time: times.isha },
  ];
}

/** Next upcoming prayer (rolls over to tomorrow's Fajr after Isha). */
export function getNextPrayer(lat: number, lng: number, now: Date = new Date()): PrayerSlot {
  const today = getDayPrayers(lat, lng, now);
  const upcoming = today.find((p) => p.time.getTime() > now.getTime());
  if (upcoming) return upcoming;
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return getDayPrayers(lat, lng, tomorrow)[0];
}

/** "2:35" style remaining-time label (hours:minutes, hours omitted when zero). */
export function formatRemaining(target: Date, now: Date = new Date()): string {
  const totalMin = Math.max(0, Math.round((target.getTime() - now.getTime()) / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}` : `${m}`;
}

export function formatClock(d: Date, locale: string): string {
  try {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
}

export function dayKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
